import { useState, useEffect } from 'react';
import { LayoutGrid } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import type { GridSummary } from '../utils/discoveryGrid';
import { BLOCKED_FILL, LEVEL_FILL } from './DiscoveryGrid';

/**
 * Engagement-card thumbnail of the discovery grid: an ordered 3x3 filled from
 * the grid summary, blocked first, then dark (locked) to light (not started),
 * in proportion to the real counts. No shuffle. Same endpoint as the full grid
 * (docs/DISCOVERY_GRID_REDESIGN_SPEC.md §7).
 */

interface ProgressGridIconProps {
  companyId?: string;
  engagementId?: string;
  accessToken: string;
  onClick: () => void;
  className?: string;
  /** Called with the loaded summary (or null when the load failed) so hosts can show the same numbers. */
  onSummary?: (summary: GridSummary | null) => void;
}

const CELLS = 9;

/** Distribute `CELLS` squares across the buckets proportionally; every non-empty bucket gets at least one. */
export function thumbnailCells(summary: GridSummary | null): string[] {
  if (!summary || summary.total === 0) return Array(CELLS).fill(LEVEL_FILL[0]);
  const buckets: Array<{ fill: string; count: number }> = [
    { fill: BLOCKED_FILL, count: summary.blocked },
    { fill: LEVEL_FILL[5], count: summary.byLevel[5] },
    { fill: LEVEL_FILL[4], count: summary.byLevel[4] },
    { fill: LEVEL_FILL[3], count: summary.byLevel[3] },
    { fill: LEVEL_FILL[2], count: summary.byLevel[2] },
    { fill: LEVEL_FILL[1], count: summary.byLevel[1] },
    { fill: LEVEL_FILL[0], count: summary.byLevel[0] },
  ];
  // Blocked cells are also counted in their level; weight them out of the level buckets so the total stays `summary.total`.
  const denominator = Math.max(1, summary.total + summary.blocked);
  const allotted = buckets.map((b) => (b.count > 0 ? Math.max(1, Math.floor((b.count / denominator) * CELLS)) : 0));
  let used = allotted.reduce((a, b) => a + b, 0);
  // Trim or pad from the largest buckets until exactly CELLS.
  while (used > CELLS) {
    const i = allotted.indexOf(Math.max(...allotted.filter((n, idx) => n > 1 || buckets[idx].count === 0)));
    if (i === -1) break;
    allotted[i]--;
    used--;
  }
  while (used < CELLS) {
    let best = -1;
    let bestGap = -Infinity;
    buckets.forEach((b, i) => {
      if (b.count === 0) return;
      const gap = (b.count / denominator) * CELLS - allotted[i];
      if (gap > bestGap) { bestGap = gap; best = i; }
    });
    if (best === -1) break;
    allotted[best]++;
    used++;
  }
  const out: string[] = [];
  buckets.forEach((b, i) => { for (let n = 0; n < allotted[i]; n++) out.push(b.fill); });
  return out.slice(0, CELLS);
}

export function ProgressGridIcon({ companyId, engagementId, accessToken, onClick, className = '', onSummary }: ProgressGridIconProps) {
  const [summary, setSummary] = useState<GridSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!companyId) { setLoading(false); return; }
      try {
        const url = new URL(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/discovery-grid/${companyId}`);
        if (engagementId) url.searchParams.set('engagementId', engagementId);
        const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${accessToken}` } });
        if (!res.ok) throw new Error(res.statusText);
        const json = await res.json();
        if (!cancelled && json?.summary) {
          setSummary(json.summary as GridSummary);
          onSummary?.(json.summary as GridSummary);
        }
      } catch (error) {
        console.error('[ProgressGridIcon] Error loading grid summary:', error);
        if (!cancelled) onSummary?.(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [companyId, engagementId, accessToken]);

  if (loading) {
    return (
      <button onClick={onClick} className={`p-2 rounded-md hover:bg-gray-100 transition-colors ${className}`} title="View Discovery Grid">
        <LayoutGrid className="size-5 text-gray-400" />
      </button>
    );
  }

  const cells = thumbnailCells(summary);
  const hasBlockers = (summary?.blocked ?? 0) > 0;
  const title = summary
    ? `View Discovery Grid (${summary.percentLocked}% locked${hasBlockers ? `, ${summary.blocked} blocked` : ''})`
    : 'View Discovery Grid';

  return (
    <button
      onClick={onClick}
      className={`relative p-2 rounded-md hover:bg-gray-100 transition-colors ${className}`}
      title={title}
      aria-label={title}
    >
      <div className="flex flex-col gap-0.5" aria-hidden="true">
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex gap-0.5">
            {[0, 1, 2].map((col) => (
              <div key={col} className="w-1.5 h-1.5 rounded-[1px]" style={{ backgroundColor: cells[row * 3 + col] ?? LEVEL_FILL[0] }} />
            ))}
          </div>
        ))}
      </div>
      {hasBlockers && <div className="absolute inset-0 rounded-md border-2 border-amber-400 pointer-events-none" />}
    </button>
  );
}
